import { useNavigate } from 'react-router-dom';

import { Box, Button, Container, Flex, Text } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';

import { WAITLIST_MODE } from '@/config/featureFlags';
import { T } from '@/theme/assay';

// ─── Hero animation timings (seconds, matched to the design prototype) ──────
const T_STRATA = 0.4;
const T_EYEBROW = 1.4;
const T_WORDMARK = 2;
const T_HEADLINE = 3.9;
const T_SUB = 4.9;
const T_CTAS = 5.8;
const T_SECONDARY = 6.3;
const T_SCROLL_CUE = 6.6;

const WORDMARK_SWEEP_DUR = 1.4;

const STRATA_LINES = [
  { y: 0.15, opacity: 0.05 },
  { y: 0.28, opacity: 0.06 },
  { y: 0.41, opacity: 0.07 },
  { y: 0.54, opacity: 0.32 }, // emphasis line - golden-ratio sweet spot
  { y: 0.67, opacity: 0.09 },
  { y: 0.8, opacity: 0.06 },
  { y: 0.92, opacity: 0.05 },
];

const HEADLINE = "The personal CRM that tells you who's slipping away.";

// ─── Component ────────────────────────────────────────────────────────────────

export function HeroSection() {
  const navigate = useNavigate();
  const headlineWords = HEADLINE.split(' ');

  return (
    <Box
      component="section"
      aria-label="Introduction"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        paddingTop: 80,
      }}
    >
      {/* Background geological gradients */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(ellipse at 15% 60%, rgba(196, 152, 58, 0.06) 0%, transparent 45%),
            radial-gradient(ellipse at 85% 30%, rgba(58, 140, 128, 0.04) 0%, transparent 40%),
            radial-gradient(ellipse at 50% 100%, rgba(196, 152, 58, 0.04) 0%, transparent 50%)
          `,
          pointerEvents: 'none',
        }}
      />
      {/* Strata decoration lines - right side, drawn in left from the right edge */}
      <Box
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 120,
          pointerEvents: 'none',
        }}
      >
        {STRATA_LINES.map((line, i) => (
          <Box
            key={line.y}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: `${line.y * 100}%`,
              height: 1,
              background: `rgba(196, 152, 58, ${line.opacity})`,
              transformOrigin: 'right',
              animation: `assay-strata-line-draw 1.6s cubic-bezier(0.22, 1, 0.36, 1) ${T_STRATA + i * 0.08}s both`,
            }}
          />
        ))}
        <Box
          style={{
            position: 'absolute',
            left: 40,
            top: 0,
            bottom: 0,
            width: 1,
            background: `linear-gradient(to bottom, transparent 0%, rgba(196,152,58,0.18) 30%, rgba(196,152,58,0.32) 50%, rgba(196,152,58,0.18) 70%, transparent 100%)`,
            opacity: 0,
            animation: `assay-fade-in 1.4s ease ${T_STRATA + 0.4}s both`,
          }}
        />
      </Box>
      <Container size="xl">
        <Box style={{ maxWidth: 820 }}>
          {/* Pre-title label - rule grows then text fades in */}
          <Flex align="center" gap={10} mb={28}>
            <Box
              style={{
                width: 32,
                height: 1,
                background: T.ore,
                animation: `assay-eyebrow-rule-draw 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${T_EYEBROW}s both`,
              }}
            />
            <Box
              component="span"
              style={{
                fontFamily: '"DM Mono", monospace',
                fontSize: '0.6rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: T.ore,
                animation: `assay-word-rise 0.55s ease ${T_EYEBROW + 0.25}s both`,
              }}
            >
              Personal CRM
            </Box>
          </Flex>

          {/* Wordmark - bottom-up clip-path reveal + travelling gold sweep line */}
          <Box style={{ position: 'relative', marginBottom: '1.5rem' }}>
            <Box
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontWeight: 560,
                fontSize: 'clamp(5rem, 14vw, 10rem)',
                color: T.textPrimary,
                letterSpacing: '-0.07em',
                lineHeight: 0.9,
                textTransform: 'uppercase',
                clipPath: 'inset(100% 0 0 0)',
                animation: `assay-wordmark-reveal ${WORDMARK_SWEEP_DUR}s cubic-bezier(0.22, 1, 0.36, 1) ${T_WORDMARK}s both`,
              }}
            >
              Assay
            </Box>
            {/* Travelling gold "discovery" line - disappears after the wipe completes */}
            <Box
              aria-hidden
              style={{
                position: 'absolute',
                left: -10,
                right: -10,
                top: '100%',
                height: 2,
                background: T.oreLight,
                boxShadow: `0 0 24px ${T.oreLight}, 0 0 8px ${T.oreLight}`,
                pointerEvents: 'none',
                opacity: 0,
                animation: `assay-wordmark-sweep ${WORDMARK_SWEEP_DUR}s cubic-bezier(0.22, 1, 0.36, 1) ${T_WORDMARK}s both`,
              }}
            />
          </Box>

          {/* Headline - word-by-word fade-up */}
          <Box
            component="h1"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontWeight: 300,
              fontSize: 'clamp(2.2rem, 4vw, 3.5rem)',
              color: T.textPrimary,
              lineHeight: 1.1,
              margin: '0 0 1.5rem 0',
              letterSpacing: '-0.02em',
            }}
          >
            {headlineWords.map((word, i) => (
              <span key={`${word}-${i}`}>
                <span
                  style={{
                    display: 'inline-block',
                    opacity: 0,
                    animation: `assay-word-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${T_HEADLINE + i * 0.06}s both`,
                  }}
                >
                  {word}
                </span>
                {i < headlineWords.length - 1 ? ' ' : ''}
              </span>
            ))}
          </Box>

          {/* Sub copy */}
          <Text
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: '0.9rem',
              color: T.textMuted,
              lineHeight: 1.7,
              maxWidth: 520,
              marginBottom: '2.5rem',
              opacity: 0,
              animation: `assay-word-rise 0.7s ease ${T_SUB}s both`,
            }}
          >
            Assay tracks who you&apos;re losing touch with, nudges you once a week, and drafts the
            check-in in your own voice - from your real history with that person. No hype, no AI
            slop, no open-rate leaderboards.
          </Text>

          {/* CTAs */}
          <Flex
            gap={12}
            style={{
              opacity: 0,
              animation: `assay-word-rise 0.7s ease ${T_CTAS}s both`,
            }}
          >
            <Button
              variant="filled"
              size="md"
              rightSection={<IconChevronRight size={14} />}
              onClick={() => navigate('/signup')}
            >
              {WAITLIST_MODE ? 'Join the waitlist' : 'Start for free'}
            </Button>
            <Button variant="outline" size="md" component="a" href="#how-it-works">
              See how it works
            </Button>
          </Flex>
          <Text
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: '0.75rem',
              color: T.textSecondary,
              marginTop: '1.5rem',
              maxWidth: 520,
              opacity: 0,
              animation: `assay-word-rise 0.7s ease ${T_SECONDARY}s both`,
            }}
          >
            For founders, consultants and anyone whose next opportunity comes from someone they
            already know. Assay works the relationships you already have - no cold lists, no CRM
            busywork. Free to start.
          </Text>
        </Box>
      </Container>

      {/* Bottom scroll cue */}
      <Box
        style={{
          position: 'absolute',
          bottom: 32,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          opacity: 0,
          animation: `assay-fade-in 0.8s ease ${T_SCROLL_CUE}s both`,
        }}
      >
        <Box
          style={{
            width: 1,
            height: 48,
            background: `linear-gradient(to bottom, ${T.ore}60, transparent)`,
          }}
        />
        <Box
          component="span"
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: '0.5rem',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: T.textMuted,
          }}
        >
          Scroll
        </Box>
      </Box>
    </Box>
  );
}
